// Адаптер журнала операций корп. кошелька (GET /users/corp_clients/{id}/wallet-transactions/).
// Единственное место, которое знает формат записей журнала и превращает их в понятные операции.
//
// Правила бэка:
// - operation_type — сохранённый тип записи, а не направление: входящий перевод тоже может прийти с кодом 21.
//   Направление определяем только по плательщику (payer) и получателю (recipient);
// - участники приходят объектами { type, id, name, owner }; получатель может быть null,
//   у удалённого участника name = null, но тип и id сохранены;
// - у старых записей может не быть nomenclature_id и названия теста — тогда есть только код;
// - служебный менеджер корпа (owner = 'buffer') — это буфер компании.

// Типы записи для фильтра (operation_type бэка)
export const OPERATION_TYPES = [
  { value: 10, text: 'Приход' },
  { value: 11, text: 'Пополнение' },
  { value: 20, text: 'Расход' },
  { value: 21, text: 'Списание' },
];

export const REPLENISHMENT_SOURCES = {
  catalog_order: 'Заказ из каталога',
  manual_admin: 'Вручную админом',
};

const PARTICIPANT_TITLES = {
  admin: 'Администратор',
  partner: 'Партнёр',
  candidate: 'Кандидат',
  corpclient: 'Буфер компании',
  manager: 'Менеджер',
};

// Участники самого корпа: его буфер и его менеджеры. Всё остальное — «снаружи» компании.
const isInside = (p) => !!p && (p.type === 'corpclient' || p.type === 'manager');
const isBuffer = (p) => !!p && (p.type === 'corpclient' || p.owner === 'buffer');

function parseParticipant(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const base = {
    type: raw.type || '',
    id: raw.id != null ? raw.id : null,
    owner: raw.owner || null,
    name: raw.name || '',
  };
  let title;
  if (isBuffer(base)) title = 'Буфер компании';
  else if (base.name) title = base.name;
  else if (base.type) title = `${PARTICIPANT_TITLES[base.type] || base.type} (удалён)`;
  else title = '—';
  // Подпись роли под именем, если имя не говорит само за себя
  // У удалённого участника роль уже есть в названии
  const role = isBuffer(base) || !base.name ? '' : (PARTICIPANT_TITLES[base.type] || base.type);
  return { ...base, title, role };
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * Направление и название операции относительно кошелька компании.
 * direction: 'in' — пришло в компанию, 'out' — ушло из компании, 'transfer' — внутри компании, 'other' — не касается кошелька.
 */
export function describeOperation(payer, recipient) {
  const from = isInside(payer);
  const to = isInside(recipient);
  if (!from && to) {
    return { direction: 'in', title: isBuffer(recipient) ? 'Зачисление в буфер' : 'Зачисление менеджеру' };
  }
  if (from && to) {
    // Буфер корпа хранится на служебном менеджере: перенос между ним и самим корпом остатка компании не меняет
    if (isBuffer(payer) && isBuffer(recipient)) return { direction: 'transfer', title: 'Перенос внутри буфера' };
    return { direction: 'transfer', title: isBuffer(recipient) ? 'Возврат в буфер' : 'Передача менеджеру' };
  }
  if (from && !to) {
    return { direction: 'out', title: recipient && recipient.type === 'candidate' ? 'Назначение кандидату' : 'Списание' };
  }
  return { direction: 'other', title: 'Операция' };
}

// Служебные комментарии скриптов («consolidate_... run=<uuid>») админу и корпу ничего не говорят
const isTechnicalComment = (text) => /\brun=[0-9a-f-]{8,}/i.test(text);

function signedQty(direction, qty) {
  if (!qty) return null;
  if (direction === 'in') return qty;
  if (direction === 'out') return -qty;
  return null;
}

function parseOperation(raw) {
  const payer = parseParticipant(raw.payer);
  const recipient = parseParticipant(raw.recipient);
  const { direction, title } = describeOperation(payer, recipient);
  const qty = num(raw.qty);
  const comment = typeof raw.comment === 'string' ? raw.comment.trim() : '';
  const time = typeof raw.time === 'string' ? raw.time.slice(0, 5) : '';
  return {
    id: raw.id,
    // Дата и время самой операции; если их нет — время создания записи
    date: raw.date || (raw.created_at ? String(raw.created_at).slice(0, 10) : ''),
    time,
    createdAt: raw.created_at || '',
    title,
    direction,
    // Пополнение буфера компании (а не передача менеджеру)
    toBuffer: direction === 'in' && isBuffer(recipient),
    qty,
    // Знак показываем только там, где кошелёк компании реально изменился
    signedQty: signedQty(direction, qty),
    typeCode: raw.operation_type != null ? raw.operation_type : null,
    typeLabel: raw.operation_type_label || '',
    testName: raw.nomenclature_name || raw.nomenclature_code || '—',
    nomenclatureId: raw.nomenclature_id != null ? raw.nomenclature_id : null,
    initiator: parseParticipant(raw.initiator),
    payer,
    recipient,
    purchaseId: raw.purchase_id != null ? raw.purchase_id : null,
    source: raw.replenishment_source || null,
    sourceLabel: REPLENISHMENT_SOURCES[raw.replenishment_source] || '',
    comment: comment && !isTechnicalComment(comment) ? comment : '',
  };
}

/**
 * Ответ журнала → { count, items }.
 * Обычный ответ — { count, next, previous, results }, при page_size=-1 — сразу массив.
 */
export function parseWalletTransactions(raw) {
  let list = [];
  if (Array.isArray(raw)) list = raw;
  else if (raw && Array.isArray(raw.results)) list = raw.results;
  const items = list.filter((x) => x && typeof x === 'object').map(parseOperation);
  const paged = !Array.isArray(raw) && raw && Number.isFinite(Number(raw.count));
  return { count: paged ? Number(raw.count) : items.length, items };
}
